import { useRef, useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Pin, PinOff, Settings2, Target, X } from 'lucide-react'
import { useExecDashboard } from '../../contexts/ExecDashboardContext'
import type { DashboardWidget } from '../../types/exec'
import WidgetConfigEditor from './WidgetConfigEditor'
import WidgetInfoButton from './WidgetInfoButton'
import WidgetExportMenu from './WidgetExportMenu'
import GoalStatusChip from './GoalStatusChip'
import { getWidgetExportRows, useWidgetExportRows } from './widgetDataRegistry'

interface Props {
  widget: DashboardWidget
  onRemove: (widgetId: string) => void
  onUpdate: (widgetId: string, data: Partial<DashboardWidget>) => void
  children: React.ReactNode
}

export default function WidgetCard({ widget, onRemove, onUpdate, children }: Props) {
  const { measures, goals } = useExecDashboard()
  const [showConfirmRemove, setShowConfirmRemove] = useState(false)
  const [showEditor, setShowEditor] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  const measureDef = measures[widget.measure]
  const displayTitle = widget.title || measureDef?.label || widget.measure

  // Find matching goal for this widget's measure. Prefer a goal scoped to
  // this widget's portfolio_group when both are scoped — falls back to a
  // global goal on the same measure.
  const matchingGoal = useMemo(() => {
    const widgetPg = (widget.config?.portfolio_group as string | undefined) ?? null
    const candidates = goals.filter((g) => g.measure === widget.measure)
    if (widgetPg) {
      const exact = candidates.find((g) => g.portfolio_group === widgetPg)
      if (exact) return exact
    }
    return candidates.find((g) => !g.portfolio_group) || candidates[0]
  }, [goals, widget.measure, widget.config?.portfolio_group])

  // Subscribe to the data registry so the goal status chip stays in sync
  // when the underlying widget data refreshes. We pull the latest +
  // prior value for the widget's primary metric so GoalStatusChip can
  // emit the direction-aware status (On Track / Behind / Above /
  // Behind Improving / On Target Flat). Skipped when there is no goal.
  const widgetRows = useWidgetExportRows(widget.id)
  const { latestValue, priorValue } = useMemo(() => {
    if (!matchingGoal || !widgetRows || widgetRows.length === 0) {
      return { latestValue: undefined as number | undefined, priorValue: undefined as number | undefined }
    }
    // Resolve which row column to read. Prefer the widget's primary
    // metric; fall back to the measure's kpi_field if the widget config
    // doesn't pin one.
    const metric = (Array.isArray(widget.config?.metrics) && widget.config.metrics.length > 0)
      ? (widget.config.metrics[0] as string)
      : (measureDef?.kpi_field || undefined)
    if (!metric) return { latestValue: undefined, priorValue: undefined }

    // Sort by fiscal_year descending so [0] is the latest, [1] the prior.
    const sorted = [...widgetRows].sort((a, b) => {
      const ay = Number(a.fiscal_year ?? 0)
      const by = Number(b.fiscal_year ?? 0)
      return by - ay
    })
    const numericOrUndef = (v: unknown): number | undefined => {
      if (v === null || v === undefined) return undefined
      const n = typeof v === 'number' ? v : parseFloat(String(v))
      return Number.isNaN(n) ? undefined : n
    }
    return {
      latestValue: numericOrUndef(sorted[0]?.[metric]),
      priorValue: numericOrUndef(sorted[1]?.[metric]),
    }
  }, [matchingGoal, widgetRows, widget.config, measureDef])

  return (
    <>
      <div
        ref={cardRef}
        className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-700 flex flex-col h-full overflow-hidden"
      >
        {/* Header — title area is the drag handle for react-grid-layout */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-800 shrink-0">
          <div className="flex items-center gap-2 min-w-0 widget-drag-handle cursor-grab active:cursor-grabbing flex-1">
            <div className="min-w-0">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {displayTitle}
              </h3>
              {measureDef?.category && (
                <span className="text-[10px] text-gray-400 uppercase tracking-wide">
                  {measureDef.category}
                </span>
              )}
            </div>
            {matchingGoal && (
              <div className="flex items-center gap-1 shrink-0">
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                  title={`Goal: ${matchingGoal.target_value.direction === 'lower_is_better' ? '≤' : '≥'} ${matchingGoal.target_value.value} ${matchingGoal.target_value.unit}`}
                >
                  <Target size={10} />
                  {matchingGoal.target_value.direction === 'lower_is_better' ? '≤' : '≥'}
                  {matchingGoal.target_value.value}
                  {matchingGoal.target_value.unit === 'percent' ? '%' : ''}
                  {matchingGoal.target_value.unit === 'days' ? 'd' : ''}
                </span>
                {/* Direction-aware status (On Track / Behind / Above /
                    Behind Improving / On Target Flat) — only renders when
                    we have a latest value to compare against. */}
                <GoalStatusChip
                  goal={matchingGoal}
                  latestValue={latestValue}
                  priorValue={priorValue}
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-0.5 shrink-0 ml-2">
            <WidgetInfoButton measure={widget.measure} title={displayTitle} />

            <WidgetExportMenu
              title={displayTitle}
              cardRef={cardRef}
              csvRows={getWidgetExportRows(widget.id)}
            />

            <button
              onClick={() => setShowEditor(true)}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-blue-600"
              title="Edit widget"
            >
              <Settings2 size={14} />
            </button>

            <button
              onClick={() => onUpdate(widget.id, { pinned: !widget.pinned })}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600"
              title={widget.pinned ? 'Unpin' : 'Pin to top'}
            >
              {widget.pinned ? <PinOff size={14} /> : <Pin size={14} />}
            </button>

            {showConfirmRemove ? (
              <div className="flex items-center gap-1 text-xs">
                <button
                  onClick={() => onRemove(widget.id)}
                  className="px-1.5 py-0.5 rounded bg-red-500 text-white hover:bg-red-600"
                >
                  Remove
                </button>
                <button
                  onClick={() => setShowConfirmRemove(false)}
                  className="px-1.5 py-0.5 rounded text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowConfirmRemove(true)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-red-500"
                title="Remove widget"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-2 min-h-0">
          {children}
        </div>
      </div>

      {/* Config Editor Modal — portaled to body to escape grid z-index */}
      {showEditor && createPortal(
        <WidgetConfigEditor
          widget={widget}
          onClose={() => setShowEditor(false)}
          onSave={(updates) => {
            onUpdate(widget.id, updates)
            setShowEditor(false)
          }}
        />,
        document.body,
      )}
    </>
  )
}
